import { toBase62 } from "../../../_utils/encoding.utils";

/**
 * De-duplicates files that share the same name across different sub-folders.
 *
 * - The first occurrence of a file name is returned unchanged.
 * - Subsequent duplicates are renamed using a Base62-encoded prefix derived from their parent folder path.
 * - Also updates the `webkitRelativePath` to reflect the new file name while preserving the original folder structure.
 *
 * This is useful when uploading directories where multiple files may have identical names but exist in different subdirectories.
 *
 * @param files Array of File objects (e.g., from input[type="file"] with webkitdirectory)
 *
 * @returns A new array of File objects with unique file names
 */
export function dedupeFilesByName(files: File[]): File[] {
  const fileNameCountMap = new Map<string, number>();

  return files.map((file) => {
    let count = fileNameCountMap.get(file.name) ?? 0;

    count++;

    fileNameCountMap.set(file.name, count);

    // First occurrence of file name => keep as-is
    if (count === 1) {
      return file;
    }

    // Duplicate file name => rename (file name and webkitRelativePath)
    return createDedupedFile(file);
  });
}

function createDedupedFile(file: File): File {
  const webkitRelativePath = file.webkitRelativePath || "";

  const folderName = webkitRelativePath
    .split("/")
    .slice(0, -1)
    .join("/");

  const encodedSubFolder = toBase62(folderName);

  const dedupedFileName = `${encodedSubFolder}__${file.name}`;

  const dedupedWebkitRelativePath = webkitRelativePath
    ? `${folderName}/${encodedSubFolder}__${file.name}`
    : "";

  const readOnlyAttributeProps = {
    writable: false,
    enumerable: true,
    configurable: false,
  };

  const dedupedFile = new File([file], dedupedFileName, {
    type: file.type,
    lastModified: file.lastModified,
  });

  Object.defineProperty(dedupedFile, "webkitRelativePath", {
    value: dedupedWebkitRelativePath,
    ...readOnlyAttributeProps,
  });

  return dedupedFile;
}
